import type { FC } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { LogoutButton } from "./components/Auth/AuthComponents";
import MessageLimitReached from "./components/Auth/MessageLimitReached";
import ChatBox from "./components/ChatBox/ChatBox";
import type { ChatMessage } from "./components/ChatBox/Messages";
import SessionsOverview from "./components/SessionsOverview";
import StartPage from "./components/StartPage";
import TabBar from "./components/TabBar";
import Flowsheet from "./components/Tabs/Flowsheet/Flowsheet";
import Optimization from "./components/Tabs/Optimization/OptimizationTab";
import PDFViewer from "./components/Tabs/PDF/PDFViewer";
import PlotGrid from "./components/Tabs/Plot/PlotGrid";
import PredictorModel from "./components/Tabs/Predictor Model/PredictorModel";
import DataTable from "./components/Tabs/Table/DataTable";
import { useApiAuth } from "./hooks/useApiAuth";
import { useAuthenticatedUser } from "./hooks/useAuthenticatedUser";
import { useChatSession } from "./hooks/useChatSession";
import { useMessageManager } from "./hooks/useMessageManager";
import { useSessionCache, useSessionStore } from "./stores/sessionStore";
import type { TabItem } from "./types/tabs";

const App: FC = () => {
  const [pendingMessage, setPendingMessage] = useState<ChatMessage | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isChatExpanded, setIsChatExpanded] = useState(false);

  const { getUserId } = useAuthenticatedUser();
  const { getAuthHeaders } = useApiAuth();
  const userID = getUserId() || "anonymous";

  const chatSession = useChatSession({ sessionId, userID });
  const { 
    saveToBackend, 
    isSessionDirty, 
    isSaving 
  } = useSessionCache(sessionId);

  const {
    messages,
    tabs: dynamicTabs,
    activeTabId,
    view,
    tableRefreshKey,
    handleResponse,
    setView,
    setActiveTabId,
    updateMessages,
    setMessages,
    setTabs,
    removeTab
  } = useMessageManager({ sessionId, userID });

  // Derive active tab from tabs array
  const activeTab = useMemo(
    () => dynamicTabs.find(tab => tab.id === activeTabId) || dynamicTabs[0] || null,
    [dynamicTabs, activeTabId]
  );

  const handleSessionSelected = useCallback(async (sessionId: string, shouldGoToChat: boolean, shouldLoadFromBackend: boolean = false) => {
    setSessionId(sessionId);
    setView(shouldGoToChat ? "chat" : "start");
    
    // Load session cache from backend only when resuming
    if (sessionId && shouldLoadFromBackend) {
      const authHeaders = await getAuthHeaders(); // 🔒 Get auth headers
      const loadedCache = await useSessionStore.getState().loadFromBackend(sessionId, authHeaders);
      if (loadedCache) {
        // Always restore messages if they exist
        if (loadedCache.messages && loadedCache.messages.length > 0) {
          setMessages(loadedCache.messages);
        }
        
        // Restore tabs if they exist
        if (loadedCache.tabs && loadedCache.tabs.length > 0) {
          setTabs(loadedCache.tabs);
          setTimeout(() => setView("tab"), 100);
        } else if (loadedCache.messages && loadedCache.messages.length > 0) {
          // If we have messages but no tabs, go to chat view
          setTimeout(() => setView("chat"), 100);
        }
      }
    }
  }, [setMessages, setTabs, setView, getAuthHeaders]);

  // Memoize cache function to prevent infinite loops
  const stableCacheSession = useCallback((messages: ChatMessage[], tabs: TabItem[]) => {
    if (sessionId) {
      useSessionStore.getState().cacheSession(sessionId, userID, messages, tabs);
    }
  }, [sessionId, userID]);

  // Cache session when in tab or chat view with content (debounced)
  useEffect(() => {
    if ((view === "tab" || view === "chat") && sessionId && messages.length > 0) {
      const timer = setTimeout(() => {
        stableCacheSession(messages, dynamicTabs);
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [view, sessionId, messages, dynamicTabs, stableCacheSession]);

  // Auto-save to backend when session becomes dirty (only when not streaming)
  useEffect(() => {
    if (isSessionDirty && sessionId && !isSaving && chatSession.status !== 'streaming') {
      const timer = setTimeout(async () => {
        const authHeaders = await getAuthHeaders(); // 🔒 Get auth headers
        await saveToBackend(authHeaders);
      }, 2000); // Save 2 seconds after last change
      
      return () => clearTimeout(timer);
    }
  }, [isSessionDirty, sessionId, isSaving, saveToBackend, chatSession.status, getAuthHeaders]);

  // Single function to create/update pending message
  const setPendingMessageFromInput = useCallback((content: string, attachments?: any[]) => {
    if (!content.trim()) {
      setPendingMessage(null);
      return;
    }
    
    const message: ChatMessage = {
      role: 'user',
      content,
      id: `user-${Date.now()}`,
      createdAt: new Date(),
      attachment: attachments
    };
    setPendingMessage(message);
  }, []);

  // Single submit handler for both text and file uploads
  const handleSubmitAndSwitchToChat = useCallback((e?: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault();
    if (!pendingMessage?.content?.trim()) return;
    
    setView("chat");
    // ChatBox will handle the pending message
  }, [pendingMessage]);

  // Handler for file upload with message (used by ChatBox)
  const handleFileUploadWithMessage = useCallback((_message: string) => {
    // This is called by ChatBox when files are uploaded with a message
    // The message handling is already done by ChatBox, this is just a placeholder
  }, []);

  // Main content for tab layout (memoized to prevent unnecessary re-renders during streaming)
  const mainTabContent = useMemo(() => {
    if (!activeTab) return null;
    
    return activeTab.type === 'table' && activeTab.file_name ? (
      <div className="h-full flex flex-col min-h-0">
        <DataTable 
          tableFileName={activeTab.file_name} 
          sessionID={sessionId ?? undefined} 
          userID={userID}
          refreshKey={tableRefreshKey}
        />
      </div>
    ) : activeTab.type === 'pdf' && activeTab.pdf_data ? (
      <div className="h-full flex flex-col min-h-0">
        <PDFViewer
          pdfFileName={activeTab.pdf_data.pdf_file_name}
          markdownContent={activeTab.pdf_data.markdown_content}
          sessionID={sessionId ?? ''}
          userID={userID}
        />
      </div>
    ) : activeTab.type === 'flowsheet' && activeTab.flowsheet_data ? (
      <div className="w-full h-full">
        <Flowsheet flowsheetData={activeTab.flowsheet_data} />
      </div>
    ) : activeTab.type === 'data' && activeTab.plots ? (
      <div className="w-full h-full">
        <PlotGrid 
          plots={activeTab.plots} 
          sessionId={sessionId ?? undefined}
          userID={userID}
        />
      </div>
    ) : activeTab.type === 'predictor_model' && activeTab.predictor_model_data ? (
      <div className="w-full h-full overflow-hidden min-w-0 max-w-full">
        <PredictorModel 
          predictorModelData={activeTab.predictor_model_data}
          tabId={activeTab.id}
          userID={userID}
          sessionId={sessionId ?? undefined}
          images={activeTab.images || []}
          onTrainingMessage={(message: ChatMessage) => {
            // Add training message directly to chat session for immediate display
            chatSession.addExternalMessage(message);
          }}
          onResponse={handleResponse}
        />
      </div>
    ) : activeTab.type === 'optimization' && activeTab.optimization_data ? (
      <div className="w-full h-full overflow-hidden min-w-0 max-w-full">
        <Optimization 
          optimizationData={activeTab.optimization_data} 
          userID={userID}
          sessionId={sessionId ?? undefined}
          onOptimizationMessage={(message: ChatMessage) => {
            // Add optimization message directly to chat session for immediate display
            chatSession.addExternalMessage(message);
          }}
          onResponse={handleResponse}
        />
      </div>
    ) : null;
  }, [
    // Only depend on tab-specific data, NOT on streaming state
    activeTab,
    sessionId, 
    tableRefreshKey, 
    userID
    // Removed addExternalMessage and handleResponse to prevent streaming re-renders
  ]);

  return (
    <div className="h-screen w-screen flex flex-col overflow-hidden max-w-full">
      {/* Show message limit overlay if limit is reached */}
      {chatSession.hasMessageLimitError && <MessageLimitReached />}
      
      {/* Header with home and logout buttons */}
      <div className="flex justify-between items-center p-4 bg-white border-b border-gray-200">
        <div className="flex items-center gap-3">
          {view !== "sessions" && (
            <button
              onClick={() => setView("sessions")}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title="Return to case overview"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </button>
          )}
        </div>
        <LogoutButton />
      </div>
      
      {view === "sessions" && (
        <SessionsOverview
          userID={userID}
          onSessionSelected={handleSessionSelected}
        />
      )}
      {view === "start" && (
        <StartPage
          chatSession={chatSession}
          onPendingMessageChange={setPendingMessageFromInput}
          onSubmit={handleSubmitAndSwitchToChat}
          onResponse={handleResponse}
        />
      )}
      {view === "tab" ? (
        <div className="flex-1 flex overflow-hidden flex-col min-h-0 max-w-full">
          <div className="flex-1 flex overflow-hidden min-h-0 max-w-full">
            <div className="flex-1 flex flex-col bg-white h-full overflow-hidden min-w-0 max-w-full">
              <div className="flex-1 overflow-hidden min-h-0 max-w-full">
                {mainTabContent}
              </div>
            </div>
            
            <TabBar 
              tabs={dynamicTabs}
              activeTabId={activeTabId}
              onTabClick={setActiveTabId}
              onTabClose={removeTab}
            />
          </div>
          {/* Expand/collapse button above chat window */}
          <div className="flex justify-center items-center w-full -mb-1 z-20 flex-shrink-0">
            <button
              onClick={() => setIsChatExpanded(!isChatExpanded)}
              className="bg-gray-200 hover:bg-gray-300 rounded-t-lg px-3 py-1 text-xs font-medium text-gray-600 transition-colors duration-200"
            >
              {isChatExpanded ? (
                <>
                  <span className="mc-1">▼</span>
                </>
              ) : (
                <>
                  <span className="mc-1">▲</span>
                </>
              )}
            </button>
          </div>
          {/* ChatBox integrated within the tab view */}
          <div className="border-t bg-gray-50 p-4 flex-shrink-0">
            <ChatBox
              chatSession={chatSession}
              sessionId={sessionId}
              userID={userID}
              onResponse={handleResponse}
              messagesHeightClass={isChatExpanded ? "h-96" : "h-24"}
              preservedMessages={messages}
              onMessagesUpdate={updateMessages}
              onFileUploadWithMessage={handleFileUploadWithMessage}
              pendingMessage={pendingMessage}
              onPendingMessageProcessed={() => setPendingMessage(null)}
            />
          </div>
        </div>
      ) : view === "chat" ? (
        <div className="flex-1 flex flex-col min-h-0">
          <div className="flex-1 min-h-0 flex items-center justify-center bg-gray-50" />
          <div className="border-t bg-gray-50 p-4 flex-shrink-0">
            <ChatBox
              chatSession={chatSession}
              sessionId={sessionId}
              userID={userID}
              onResponse={handleResponse}
              messagesHeightClass="h-96"
              preservedMessages={messages}
              onMessagesUpdate={updateMessages}
              onFileUploadWithMessage={handleFileUploadWithMessage}
              pendingMessage={pendingMessage}
              onPendingMessageProcessed={() => setPendingMessage(null)}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default App;
